import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { icons } from './ui/Icons';
import Modal from './ui/Modal';
import Button from './ui/Button';
import { bookingSchema, formatPhone } from '../utils/validationSchemas';

/**
 * Componente: MultiSlotBookingModal
 * Modal para agendamento que ocupa múltiplos horários consecutivos
 * baseado na duração do serviço
 */
const MultiSlotBookingModal = ({ 
  isOpen, 
  onClose, 
  initialSlot,
  availableSlots,  // Array de todos os slots disponíveis do dia
  barbershopName, 
  barbershopServices, 
  onSubmit,
  ownerBookingMode = false, // Modo barbeiro: agendar por um cliente
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [requiredSlots, setRequiredSlots] = useState([]);
  const [detectedSlotInterval, setDetectedSlotInterval] = useState(30);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(bookingSchema),
    defaultValues: {
      service: '',
      date: '',
      time: '',
      clientName: '',
      clientPhone: '',
      notes: '',
    },
  });

  // Calcular slots necessários baseado na duração do serviço
  const calculateRequiredSlots = useCallback((startSlot, durationMinutes) => {
    if (!startSlot || !durationMinutes || !availableSlots || availableSlots.length === 0) {
      return { slots: [], interval: 30 };
    }

    // Ordenar slots disponíveis por horário
    const sortedSlots = [...availableSlots]
      .filter(s => s.date === startSlot.date)
      .sort((a, b) => a.time.localeCompare(b.time));

    if (sortedSlots.length === 0) {
      return { slots: [], interval: 30 };
    }

    // Detectar o intervalo entre slots (em minutos)
    let slotInterval = 60; // Padrão mais comum
    if (sortedSlots.length >= 2) {
      const firstTime = new Date(`2000-01-01T${sortedSlots[0].time}`);
      const secondTime = new Date(`2000-01-01T${sortedSlots[1].time}`);
      slotInterval = (secondTime - firstTime) / (1000 * 60);
    }

    const slotsNeeded = Math.ceil(durationMinutes / slotInterval);
    const slots = [];

    // Encontrar índice do slot inicial
    const startIndex = sortedSlots.findIndex(s => 
      s.time === startSlot.time && s.date === startSlot.date
    );

    if (startIndex === -1) {
      return { slots: [], interval: slotInterval };
    }

    // Verificar se há slots consecutivos suficientes
    for (let i = 0; i < slotsNeeded; i++) {
      const slot = sortedSlots[startIndex + i];
      if (!slot) {
        // Não há slots suficientes
        return { slots: [], interval: slotInterval };
      }

      // Verificar se o horário é consecutivo (diferença igual ao intervalo)
      if (i > 0) {
        const prevSlot = sortedSlots[startIndex + i - 1];
        const prevTime = new Date(`2000-01-01T${prevSlot.time}`);
        const currTime = new Date(`2000-01-01T${slot.time}`);
        const diffMinutes = (currTime - prevTime) / (1000 * 60);
        
        if (diffMinutes !== slotInterval) {
          // Slots não são consecutivos
          return { slots: [], interval: slotInterval };
        }
      }

      slots.push(slot);
    }

    return { slots, interval: slotInterval };
  }, [availableSlots]);

  // Atualizar slots necessários quando o serviço mudar
  useEffect(() => {
    if (selectedService && initialSlot) {
      // Extrair duração em minutos
      const durationMatch = selectedService.duration.match(/(\d+)\s*min/i);
      const durationMinutes = durationMatch ? parseInt(durationMatch[1]) : 30;
      
      console.log('Calculando slots necessários:', {
        serviceName: selectedService.name,
        durationMinutes,
        initialSlot: initialSlot.time,
        availableSlotsCount: availableSlots?.length
      });
      
      const result = calculateRequiredSlots(initialSlot, durationMinutes);
      
      console.log('Resultado do cálculo:', {
        slotsEncontrados: result.slots.length,
        intervaloDetectado: result.interval,
        slots: result.slots.map(s => s.time)
      });
      
      setRequiredSlots(result.slots);
      setDetectedSlotInterval(result.interval);
    } else {
      setRequiredSlots([initialSlot].filter(Boolean));
      // Detectar intervalo mesmo sem serviço selecionado
      if (availableSlots && availableSlots.length >= 2) {
        const sortedSlots = [...availableSlots].sort((a, b) => a.time.localeCompare(b.time));
        const firstTime = new Date(`2000-01-01T${sortedSlots[0].time}`);
        const secondTime = new Date(`2000-01-01T${sortedSlots[1].time}`);
        const interval = (secondTime - firstTime) / (1000 * 60);
        setDetectedSlotInterval(interval);
      }
    }
  }, [selectedService, initialSlot, availableSlots, calculateRequiredSlots]);

  useEffect(() => {
    // Reset form quando modal abrir
    if (initialSlot) {
      reset({
        service: '',
        date: initialSlot.date || '',
        time: initialSlot.time || '',
        clientName: '',
        clientPhone: '',
        notes: '',
      });
      setSelectedService(null);
      setRequiredSlots([initialSlot]);
      
      // Detectar intervalo inicial
      if (availableSlots && availableSlots.length >= 2) {
        const sortedSlots = [...availableSlots].sort((a, b) => a.time.localeCompare(b.time));
        const firstTime = new Date(`2000-01-01T${sortedSlots[0].time}`);
        const secondTime = new Date(`2000-01-01T${sortedSlots[1].time}`);
        const interval = (secondTime - firstTime) / (1000 * 60);
        setDetectedSlotInterval(interval);
      }
    }
  }, [initialSlot, isOpen, reset, availableSlots]);

  const onSubmitForm = async (data) => {
    // Validar se há slots suficientes
    if (requiredSlots.length === 0) {
      alert('Não há horários consecutivos suficientes para este serviço. Por favor, escolha outro horário ou serviço.');
      return;
    }

    setIsSubmitting(true);

    const appointmentData = {
      ...data,
      slots: requiredSlots, // Array de slots que serão ocupados
      servicePrice: selectedService?.price,
      serviceDuration: selectedService?.duration,
      totalDuration: requiredSlots.length * detectedSlotInterval, // minutos
    };
    
    setTimeout(() => {
      onSubmit(appointmentData);
      setIsSubmitting(false);
      onClose();
    }, 1000);
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value);
    setValue('clientPhone', formatted, { shouldValidate: true });
  };

  const handleServiceChange = (e) => {
    const serviceName = e.target.value;
    if (barbershopServices && barbershopServices.length > 0) {
      const service = barbershopServices.find(s => s.name === serviceName);
      setSelectedService(service || null);
    }
  };

  // Verificar se há slots suficientes para o serviço selecionado
  const hasEnoughSlots = useMemo(() => {
    if (!selectedService) return true;
    
    const durationMatch = selectedService.duration.match(/(\d+)\s*min/i);
    const durationMinutes = durationMatch ? parseInt(durationMatch[1]) : 30;
    
    const slotsNeeded = Math.ceil(durationMinutes / detectedSlotInterval);
    
    return requiredSlots.length >= slotsNeeded && requiredSlots.length > 0;
  }, [selectedService, requiredSlots, detectedSlotInterval]);

  if (!initialSlot) return null;

  const hasServices = barbershopServices && barbershopServices.length > 0;
  const modalTitle = ownerBookingMode ? '✂️ Agendar por um Cliente' : 'Confirmar Agendamento';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={modalTitle}>
      <div className="space-y-4">
        {ownerBookingMode ? (
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 flex items-start gap-2">
            <span className="text-lg">✂️</span>
            <div>
              <p className="text-sm font-semibold text-indigo-800">Modo Barbeiro</p>
              <p className="text-xs text-indigo-600">
                Preencha os dados do cliente abaixo. O agendamento será confirmado automaticamente.
              </p>
            </div>
          </div>
        ) : (
          <p className="text-gray-700">
            Você está agendando em <strong className="text-indigo-600">{barbershopName}</strong>.
          </p>
        )}
        
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center text-gray-800">
            {icons.calendar({ className: 'h-5 w-5 mr-2 text-indigo-600' })}
            <span className="font-semibold">
              {new Date(initialSlot.date + 'T12:00:00').toLocaleDateString('pt-BR', { 
                weekday: 'long', 
                day: 'numeric', 
                month: 'long' 
              })}
            </span>
          </div>
          <div className="flex items-center text-gray-800 mt-2">
            {icons.clock({ className: 'h-5 w-5 mr-2 text-indigo-600' })}
            <span className="font-semibold">
              {initialSlot.time}
              {requiredSlots.length > 1 && ` - ${requiredSlots[requiredSlots.length - 1].time}`}
            </span>
          </div>
          
          {/* Indicador de slots múltiplos */}
          {requiredSlots.length > 1 && (
            <div className="mt-3 pt-3 border-t border-gray-300">
              <p className="text-sm text-gray-600 font-medium mb-2">
                Horários que serão reservados:
              </p>
              <div className="flex flex-wrap gap-2">
                {requiredSlots.map((slot, index) => (
                  <span 
                    key={index} 
                    className="bg-indigo-100 text-indigo-800 text-xs font-semibold px-2 py-1 rounded"
                  >
                    {slot.time}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Aviso se não houver slots suficientes */}
        {!hasEnoughSlots && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                {icons.alert({ className: 'h-5 w-5 text-red-600 mt-0.5' })}
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-red-800">
                  Horários insuficientes
                </p>
                <p className="text-sm text-red-700 mt-1">
                  Não há horários consecutivos disponíveis para este serviço. 
                  Por favor, selecione outro horário ou serviço mais curto.
                </p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmitForm)} className="space-y-4">
          <div>
            <label htmlFor="service" className="block text-sm font-medium text-gray-700">
              Tipo de Serviço *
            </label>
            {hasServices ? (
              <>
                <select
                  id="service"
                  {...register('service')}
                  onChange={(e) => {
                    register('service').onChange(e);
                    handleServiceChange(e);
                  }}
                  className={`mt-1 block w-full border rounded-lg shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                    errors.service ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Selecione um serviço</option>
                  {barbershopServices.map((service, index) => (
                    <option key={index} value={service.name}>
                      {service.name} - R$ {typeof service.price === 'number' ? service.price.toFixed(2) : service.price} ({service.duration})
                    </option>
                  ))}
                </select>
                
                {/* Preview do serviço selecionado */}
                {selectedService && (
                  <div className="mt-3 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{selectedService.name}</p>
                        <p className="text-xs text-gray-600 mt-1">
                          Duração: {selectedService.duration}
                        </p>
                        <p className="text-xs text-gray-600">
                          Slots de {detectedSlotInterval}min necessários: {requiredSlots.length > 0 ? requiredSlots.length : (
                            (() => {
                              const durationMatch = selectedService.duration.match(/(\d+)\s*min/i);
                              const durationMinutes = durationMatch ? parseInt(durationMatch[1]) : 30;
                              return Math.ceil(durationMinutes / detectedSlotInterval);
                            })()
                          )}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-indigo-600">
                          R$ {typeof selectedService.price === 'number' ? selectedService.price.toFixed(2) : selectedService.price}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <input
                type="text"
                id="service"
                {...register('service')}
                className={`mt-1 block w-full border rounded-lg shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                  errors.service ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Ex: Cabelo, Barba, Cabelo + Barba"
              />
            )}
            {errors.service && (
              <p className="mt-1 text-sm text-red-600">{errors.service.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="clientName" className="block text-sm font-medium text-gray-700">
              {ownerBookingMode ? 'Nome do cliente *' : 'Nome Completo *'}
            </label>
            <input
              type="text"
              id="clientName"
              {...register('clientName')}
              className={`mt-1 block w-full border rounded-lg shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                errors.clientName ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder={ownerBookingMode ? 'Nome completo do cliente' : 'Seu nome completo'}
            />
            {errors.clientName && (
              <p className="mt-1 text-sm text-red-600">{errors.clientName.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="clientPhone" className="block text-sm font-medium text-gray-700">
              {ownerBookingMode ? 'Telefone/WhatsApp do cliente *' : 'Telefone/WhatsApp *'}
            </label>
            <input
              type="tel"
              id="clientPhone"
              {...register('clientPhone')}
              onChange={(e) => {
                register('clientPhone').onChange(e);
                handlePhoneChange(e);
              }}
              className={`mt-1 block w-full border rounded-lg shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                errors.clientPhone ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="(11) 99999-9999"
            />
            {errors.clientPhone && (
              <p className="mt-1 text-sm text-red-600">{errors.clientPhone.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
              Observações (Opcional)
            </label>
            <textarea
              id="notes"
              {...register('notes')}
              rows={3}
              className={`mt-1 block w-full border rounded-lg shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                errors.notes ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Alguma observação sobre o corte..."
            />
            {errors.notes && (
              <p className="mt-1 text-sm text-red-600">{errors.notes.message}</p>
            )}
          </div>
          
          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button 
              type="submit" 
              variant="primary" 
              disabled={isSubmitting || !hasEnoughSlots}
            >
              {isSubmitting
                ? 'Agendando...'
                : ownerBookingMode
                ? 'Reservar para o Cliente'
                : 'Confirmar Agendamento'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default MultiSlotBookingModal;
